import { collection, doc, setDoc, getDoc, getDocs, deleteDoc, query, orderBy, Timestamp, onSnapshot, where } from 'firebase/firestore';
import { db } from '../firebase';

export interface Clue {
  id: string;
  text: string;
  createdAt: Date;
  active: boolean;
}

export interface SeenClue {
  userId: string;
  clueId: string;
  seenAt: Date;
}

export const clueService = {
  async addClue(clue: Omit<Clue, 'id' | 'createdAt'>): Promise<string> {
    try {
      const cluesCollection = collection(db, 'clues');
      const newClueRef = doc(cluesCollection);
      const newClue: Clue = {
        id: newClueRef.id,
        text: clue.text,
        createdAt: new Date(),
        active: clue.active
      };

      await setDoc(newClueRef, {
        ...newClue,
        createdAt: Timestamp.fromDate(newClue.createdAt)
      });

      return newClueRef.id;
    } catch (error) {
      console.error('Error adding clue:', error);
      throw error;
    }
  },

  async getClue(id: string): Promise<Clue | null> {
    try {
      const clueDoc = await getDoc(doc(db, 'clues', id));
      if (clueDoc.exists()) {
        const data = clueDoc.data();
        return {
          id: clueDoc.id,
          text: data.text,
          createdAt: data.createdAt.toDate(),
          active: data.active
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting clue:', error);
      throw error;
    }
  },

  async getAllClues(): Promise<Clue[]> {
    try {
      const cluesQuery = query(collection(db, 'clues'), orderBy('createdAt', 'desc'));
      const cluesSnapshot = await getDocs(cluesQuery);

      return cluesSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          text: data.text,
          createdAt: data.createdAt.toDate(),
          active: data.active
        };
      });
    } catch (error) {
      console.error('Error getting all clues:', error);
      throw error;
    }
  },

  async getActiveClues(): Promise<Clue[]> {
    try {
      const cluesQuery = query(collection(db, 'clues'), orderBy('createdAt', 'desc'));
      const cluesSnapshot = await getDocs(cluesQuery);

      return cluesSnapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            text: data.text,
            createdAt: data.createdAt.toDate(),
            active: data.active
          };
        })
        .filter(clue => clue.active);
    } catch (error) {
      console.error('Error getting active clues:', error);
      throw error;
    }
  },

  async updateClue(id: string, updates: Partial<Omit<Clue, 'id' | 'createdAt'>>): Promise<boolean> {
    try {
      await setDoc(doc(db, 'clues', id), updates, { merge: true });
      return true;
    } catch (error) {
      console.error('Error updating clue:', error);
      return false;
    }
  },

  async deleteClue(id: string): Promise<boolean> {
    try {
      await deleteDoc(doc(db, 'clues', id));
      return true;
    } catch (error) {
      console.error('Error deleting clue:', error);
      return false;
    }
  },

  subscribeToClues(callback: (clues: Clue[]) => void): () => void {
    const cluesQuery = query(collection(db, 'clues'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(cluesQuery, (snapshot) => {
      const clues = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          text: data.text,
          createdAt: data.createdAt.toDate(),
          active: data.active
        };
      });

      callback(clues);
    });

    return unsubscribe;
  },

  subscribeToActiveClues(callback: (clues: Clue[]) => void): () => void {
    const cluesQuery = query(collection(db, 'clues'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(cluesQuery, (snapshot) => {
      const clues = snapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            text: data.text,
            createdAt: data.createdAt.toDate(),
            active: data.active
          };
        })
        .filter(clue => clue.active);

      callback(clues);
    });

    return unsubscribe;
  },

  // Mark a clue as seen by a user
  async markClueSeen(userId: string, clueId: string): Promise<boolean> {
    try {
      const seenClueRef = doc(db, 'seenClues', `${userId}_${clueId}`);
      await setDoc(seenClueRef, {
        userId,
        clueId,
        seenAt: new Date()
      });
      return true;
    } catch (error) {
      console.error('Error marking clue as seen:', error);
      return false;
    }
  },

  // Mark all clues as seen by a user
  async markAllCluesSeen(userId: string): Promise<boolean> {
    try {
      const activeClues = await this.getActiveClues();
      console.log(`Marking ${activeClues.length} active clues as seen for user ${userId}`);

      if (activeClues.length === 0) {
        return true; // No clues to mark
      }

      // Get already seen clues to avoid duplicate writes
      const seenClueIds = await this.getSeenClues(userId);

      // Only mark unseen clues
      const unseenClues = activeClues.filter(clue => !seenClueIds.includes(clue.id));
      console.log(`Found ${unseenClues.length} unseen clues to mark`);

      if (unseenClues.length === 0) {
        return true; // All clues already seen
      }

      const promises = unseenClues.map(clue => this.markClueSeen(userId, clue.id));
      const results = await Promise.all(promises);

      // Check if all operations succeeded
      const allSucceeded = results.every(result => result === true);
      console.log(`Marked all clues as seen: ${allSucceeded}`);

      return allSucceeded;
    } catch (error) {
      console.error('Error marking all clues as seen:', error);
      return false;
    }
  },

  // Get all clues seen by a user
  async getSeenClues(userId: string): Promise<string[]> {
    try {
      const seenCluesQuery = query(
        collection(db, 'seenClues'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(seenCluesQuery);
      return snapshot.docs.map(doc => doc.data().clueId);
    } catch (error) {
      console.error('Error getting seen clues:', error);
      return [];
    }
  },

  // Get the count of unseen clues for a user
  async getUnseenCluesCount(userId: string): Promise<number> {
    try {
      const activeClues = await this.getActiveClues();
      const seenClueIds = await this.getSeenClues(userId);

      // Filter active clues that are not in the seen clues list
      const unseenClues = activeClues.filter(clue => !seenClueIds.includes(clue.id));
      return unseenClues.length;
    } catch (error) {
      console.error('Error checking for unseen clues:', error);
      return 0;
    }
  },

  // Subscribe to unseen clues count for a user
  subscribeToUnseenCluesCount(userId: string, callback: (unseenCount: number) => void): () => void {
    // Subscribe to active clues
    const cluesUnsubscribe = this.subscribeToActiveClues(async (activeClues) => {
      try {
        // Get seen clues
        const seenClueIds = await this.getSeenClues(userId);

        // Filter active clues that are not in the seen clues list
        const unseenClues = activeClues.filter(clue => !seenClueIds.includes(clue.id));

        callback(unseenClues.length);
      } catch (error) {
        console.error('Error in unseen clues subscription:', error);
        callback(0);
      }
    });

    // Also subscribe to the seenClues collection for this user
    const seenCluesQuery = query(
      collection(db, 'seenClues'),
      where('userId', '==', userId)
    );

    const seenCluesUnsubscribe = onSnapshot(seenCluesQuery, async () => {
      try {
        // Get active clues and seen clues
        const activeClues = await this.getActiveClues();
        const seenClueIds = await this.getSeenClues(userId);

        // Filter active clues that are not in the seen clues list
        const unseenClues = activeClues.filter(clue => !seenClueIds.includes(clue.id));

        callback(unseenClues.length);
      } catch (error) {
        console.error('Error in seenClues subscription:', error);
        callback(0);
      }
    });

    // Return a function that unsubscribes from both
    return () => {
      cluesUnsubscribe();
      seenCluesUnsubscribe();
    };
  }
};
